import { useCallback, useEffect, useMemo, useState } from "react";

import { formatTimeAgo, useAutosave } from "@/lib/hooks/use-autosave";
import {
  type Education,
  useAdminEducation,
  useCreateEducation,
  useDeleteEducation,
  useReorderEducation,
  useUpdateEducation,
} from "@/lib/queries/use-education";

interface EducationFormData {
  institution: string;
  degree: string;
  period: string;
  notes: string;
}

const emptyForm: EducationFormData = {
  institution: "",
  degree: "",
  period: "",
  notes: "",
};

export function AdminEducation() {
  const { data: education, isLoading } = useAdminEducation();
  const createEducation = useCreateEducation();
  const updateEducation = useUpdateEducation();
  const deleteEducation = useDeleteEducation();
  const reorderEducation = useReorderEducation();

  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [formData, setFormData] = useState<EducationFormData>(emptyForm);
  const [localEducation, setLocalEducation] = useState<Education[]>([]);

  // Sync local state with server data
  useEffect(() => {
    if (education) {
      setLocalEducation(education);
    }
  }, [education]);

  // Autosave for form
  const handleAutosave = useCallback(async () => {
    if (editingId === null || editingId === "new") return;

    await updateEducation.mutateAsync({
      id: editingId,
      institution: formData.institution,
      degree: formData.degree,
      period: formData.period,
      notes: formData.notes || undefined,
    });
  }, [editingId, formData, updateEducation]);

  const autosaveData = useMemo(() => formData, [formData]);

  const { lastSaved, isSaving, hasUnsavedChanges, saveNow, markAsSaved } = useAutosave({
    data: autosaveData,
    onSave: handleAutosave,
    enabled: editingId !== null && editingId !== "new",
  });

  const handleEdit = (item: Education) => {
    setEditingId(item.id);
    setFormData({
      institution: item.institution,
      degree: item.degree,
      period: item.period,
      notes: item.notes || "",
    });
    markAsSaved();
  };

  const handleNew = () => {
    setEditingId("new");
    setFormData(emptyForm);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (editingId === "new") {
      const maxOrder = localEducation.reduce((max, e) => Math.max(max, e.display_order), -1);
      await createEducation.mutateAsync({
        institution: formData.institution,
        degree: formData.degree,
        period: formData.period,
        notes: formData.notes || undefined,
        display_order: maxOrder + 1,
      });
    } else if (editingId !== null) {
      await updateEducation.mutateAsync({
        id: editingId,
        institution: formData.institution,
        degree: formData.degree,
        period: formData.period,
        notes: formData.notes || undefined,
      });
    }
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this education entry?")) {
      await deleteEducation.mutateAsync(id);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...localEducation];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalEducation(newOrder);
    await reorderEducation.mutateAsync(newOrder.map((e) => e.id));
  };

  const handleMoveDown = async (index: number) => {
    if (index === localEducation.length - 1) return;
    const newOrder = [...localEducation];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalEducation(newOrder);
    await reorderEducation.mutateAsync(newOrder.map((e) => e.id));
  };

  // Status indicator
  const SaveStatus = () => {
    if (editingId === "new") return null;

    if (isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-yellow-500 animate-pulse" />
          <span className="text-[11px] text-text-muted">Saving...</span>
        </div>
      );
    }

    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-orange-500" />
          <span className="text-[11px] text-text-muted">Unsaved changes</span>
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-green-500" />
          <span className="text-[11px] text-text-muted">Saved {formatTimeAgo(lastSaved)}</span>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div>
        <p className="text-text-muted">Loading education...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Education</h1>
        {editingId === null ? (
          <button
            onClick={handleNew}
            className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity"
          >
            + Add Education
          </button>
        ) : (
          <div className="flex items-center gap-4">
            <SaveStatus />
            {editingId !== "new" && (
              <button
                onClick={saveNow}
                disabled={isSaving || !hasUnsavedChanges}
                className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Form */}
      {editingId !== null && (
        <div className="border border-border bg-card p-4 md:p-6 mb-6">
          <h2 className="text-[11px] text-text-muted font-bold tracking-caps uppercase mb-4">
            {editingId === "new" ? "New Education" : "Edit Education"}
          </h2>

          <div className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Institution
                </label>
                <input
                  type="text"
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., Stanford University"
                />
              </div>
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Degree
                </label>
                <input
                  type="text"
                  value={formData.degree}
                  onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., B.S. Computer Science"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Period
              </label>
              <input
                type="text"
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors max-w-xs"
                placeholder="e.g., 2019 - 2023"
              />
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Notes (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Activities, honors, or other notes..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={!formData.institution || !formData.degree || !formData.period}
                className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId === "new" ? "Create" : "Save"}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-border text-text-secondary text-[13px] hover:bg-card active:bg-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Education List */}
      <div className="space-y-3">
        {localEducation.map((item, index) => (
          <div
            key={item.id}
            className="border border-border bg-card p-4 hover:border-border-hover transition-colors"
          >
            <div className="flex items-start gap-3">
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-2 transition-colors"
                  title="Move up"
                >
                  <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === localEducation.length - 1}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-2 transition-colors"
                  title="Move down"
                >
                  <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <h3 className="text-[15px] font-bold text-text-primary">{item.institution}</h3>
                  <span className="text-[11px] text-text-faint">{item.period}</span>
                </div>
                <p className="text-[13px] text-text-tertiary mb-1">{item.degree}</p>
                {item.notes && <p className="text-[12px] text-text-muted">{item.notes}</p>}
              </div>

              {/* Actions - larger touch targets */}
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => handleEdit(item)}
                  className="text-[12px] text-text-muted hover:text-primary active:text-primary/80 px-3 py-2 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-[12px] text-text-muted hover:text-destructive active:text-destructive/80 px-3 py-2 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {localEducation.length === 0 && (
          <div className="border border-dashed border-border-hover py-12 text-center">
            <p className="text-text-muted text-[14px]">No education added yet.</p>
            <button onClick={handleNew} className="mt-3 text-[13px] text-primary hover:underline">
              + Add your first entry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
